var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { faCircleCheck, faCircleExclamation, faMaximize, faMinimize } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, FormHelperText, IconButton, Link as MuiLink, Stack, TableBody, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import moment from 'moment';
import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { TABLE_HIERARCHY_COLORS } from '../../../enum';
import { formatConnectionLabel } from '../../../util';
import { Autocomplete, ConfirmDialog, ErrorMessageBox, FallbackComponent, FormField, InputLabel } from '../../common';
import { SelectOrCustomField } from '../../common/selectOrCustomField';
import { ExpandableRowLabel, TABLE_HIERARCHY_INDENTS } from '../../common/tableHierarchy';
import { useDialog } from '../../hooks';
import { WizardTable } from '../common/WizardTable';
import { MAX_TEXT_LENGTH } from '../constants';
import { OrgConfigWizardContext, useOrgConfigDirtyRegister } from '../context';
import { AddClientModal } from './AddClientModal';
import { useClientBusinessUnitsAPI, useClientLobsAPI, useClientsAPI, useCreateLobAPI, useDeleteBusinessUnitAPI, useDeleteClientAPI, useDeleteLobAPI, useDeleteSubLobAPI, useUpdateLobAPI } from './selectors';
var DialogMode;
(function (DialogMode) {
  DialogMode["AddClient"] = "AddClient";
  DialogMode["EditClient"] = "EditClient";
  DialogMode["AddIntegration"] = "AddIntegration";
  DialogMode["EditIntegration"] = "EditIntegration";
  DialogMode["DeleteClient"] = "DeleteClient";
  DialogMode["DeleteLob"] = "DeleteLob";
  DialogMode["DeleteSubLob"] = "DeleteSubLob";
  DialogMode["DeleteBu"] = "DeleteBu";
})(DialogMode || (DialogMode = {}));
var NEW_LOB_OPTION = '__new__';
var NEW_SUBLOB_OPTION = '__new_sub__';
var BLANK_LOB_FORM = {
  clientId: '',
  buId: '',
  lobSelect: '',
  lobName: '',
  subLobSelect: '',
  subLobName: ''
};
// Collapses internal whitespace runs and trims the ends so spacing variations cannot create
// duplicates. Used for the user-facing cleaned name (case preserved).
var cleanName = function (value) {
  return value.replaceAll(/\s+/g, ' ').trim();
};
// Case-insensitive + whitespace-normalized key for duplicate detection.
var normalizeName = function (value) {
  return cleanName(value).toLowerCase();
};
var is2xxSuccess = function (statusCode) {
  return statusCode != null && statusCode >= 200 && statusCode < 300;
};
// Delete-target ids for the currently open dialog. Non-matching modes resolve to 0 so the shared
// delete hooks stay inert.
var getDeleteDialogIds = function (dialogMode, dialogData) {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  return {
    deleteClientId: dialogMode === DialogMode.DeleteClient ? (_a = dialogData === null || dialogData === void 0 ? void 0 : dialogData.clientId) !== null && _a !== void 0 ? _a : 0 : 0,
    deleteLobClientId: dialogMode === DialogMode.DeleteLob ? (_b = dialogData === null || dialogData === void 0 ? void 0 : dialogData.clientId) !== null && _b !== void 0 ? _b : 0 : 0,
    deleteLobId: dialogMode === DialogMode.DeleteLob ? (_c = dialogData === null || dialogData === void 0 ? void 0 : dialogData.lobId) !== null && _c !== void 0 ? _c : 0 : 0,
    deleteBuClientId: dialogMode === DialogMode.DeleteBu ? (_d = dialogData === null || dialogData === void 0 ? void 0 : dialogData.clientId) !== null && _d !== void 0 ? _d : 0 : 0,
    deleteBuId: dialogMode === DialogMode.DeleteBu ? (_e = dialogData === null || dialogData === void 0 ? void 0 : dialogData.buId) !== null && _e !== void 0 ? _e : 0 : 0,
    deleteSubLobClientId: dialogMode === DialogMode.DeleteSubLob ? (_f = dialogData === null || dialogData === void 0 ? void 0 : dialogData.clientId) !== null && _f !== void 0 ? _f : 0 : 0,
    deleteSubLobLobId: dialogMode === DialogMode.DeleteSubLob ? (_g = dialogData === null || dialogData === void 0 ? void 0 : dialogData.lobId) !== null && _g !== void 0 ? _g : 0 : 0,
    deleteSubLobId: dialogMode === DialogMode.DeleteSubLob ? (_h = dialogData === null || dialogData === void 0 ? void 0 : dialogData.subLobId) !== null && _h !== void 0 ? _h : 0 : 0
  };
};
// On md+ the fields share one row: each flexes to an equal share and may shrink (minWidth 0)
// so all four fit without wrapping. Stacked full-width on small screens.
var ADD_LOB_FIELD_SX = {
  width: {
    xs: '100%',
    md: 'auto'
  },
  flex: {
    md: '1 1 0'
  },
  minWidth: {
    md: 0
  }
};
var AddLobPanel = styled(Box)(function (_a) {
  var theme = _a.theme;
  return {
    padding: theme.spacing(6),
    backgroundColor: theme.palette.primary.light,
    borderRadius: theme.shape.borderRadius
  };
});
// Rows are shaded by hierarchy depth so the level reads at a glance:
// Client (top level, blue) > Business Unit (medium grey) > LOB (lightest grey).
var StyledClientRow = styled(TableRow)(function () {
  return {
    '& td': {
      backgroundColor: "".concat(TABLE_HIERARCHY_COLORS.lightBlue, " !important")
    }
  };
});
var StyledBuRow = styled(TableRow)(function () {
  return {
    '& td': {
      backgroundColor: "".concat(TABLE_HIERARCHY_COLORS.lightBlueGray, " !important")
    }
  };
});
var StyledLobRow = styled(TableRow)(function () {
  return {
    '& td': {
      backgroundColor: "".concat(TABLE_HIERARCHY_COLORS.lightGray, " !important")
    }
  };
});
var StyledSubLobRow = styled(TableRow)(function () {
  return {
    '& td': {
      backgroundColor: "".concat(TABLE_HIERARCHY_COLORS.white, " !important")
    }
  };
});
var AddLobFormLayout = styled(Box)(function (_a) {
  var _b;
  var theme = _a.theme;
  return _b = {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    alignItems: 'stretch'
  }, _b[theme.breakpoints.up('md')] = {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
    columnGap: theme.spacing(4),
    rowGap: theme.spacing(4)
  }, _b;
});
var AddClientButton = styled(Button)(function (_a) {
  var theme = _a.theme;
  return {
    minWidth: theme.spacing(26),
    padding: "".concat(theme.spacing(1.5), " ").concat(theme.spacing(4)),
    borderColor: theme.palette.grey[600],
    borderWidth: 1,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.common.white,
    textTransform: 'uppercase',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.common.white
    }
  };
});
// Muted hint cell for the Integration column on BU/LOB/sub-LOB rows.
var SourceCell = function (_a) {
  var label = _a.label;
  return _jsx(TableCell, __assign({
    sx: {
      color: 'text.disabled'
    }
  }, {
    children: label
  }));
};
var IntegrationStatusIcon = function (_a) {
  var status = _a.status,
    lastConnectionTest = _a.lastConnectionTest;
  var t = useTranslation('orgConfig', {
    keyPrefix: 'clients'
  }).t;
  // Surface when the connection was last verified
  var verified = lastConnectionTest ? t('integrationLastVerified', {
    time: moment(lastConnectionTest).fromNow()
  }) : t('integrationNeverVerified');
  var tooltip = function (base) {
    return _jsxs(_Fragment, {
      children: [base, _jsx("br", {}), verified]
    });
  };
  if (status === 'connected') {
    return _jsx(Tooltip, __assign({
      title: tooltip(t('integrationConnected'))
    }, {
      children: _jsx(Box, __assign({
        component: "span",
        sx: {
          color: 'success.main'
        }
      }, {
        children: _jsx(FontAwesomeIcon, {
          icon: faCircleCheck
        })
      }))
    }));
  }
  if (status === 'failed' || status === 'not_tested' || status === null) {
    return _jsx(Tooltip, __assign({
      title: tooltip(t('integrationNotConnected'))
    }, {
      children: _jsx(Box, __assign({
        component: "span",
        sx: {
          color: 'error.main'
        }
      }, {
        children: _jsx(FontAwesomeIcon, {
          icon: faCircleExclamation
        })
      }))
    }));
  }
  return null;
};
function ClientRows(_a) {
  var _b, _c, _d;
  var client = _a.client,
    onEdit = _a.onEdit,
    onAddIntegration = _a.onAddIntegration,
    onEditIntegration = _a.onEditIntegration,
    onDelete = _a.onDelete,
    onDeleteLob = _a.onDeleteLob,
    onDeleteSubLob = _a.onDeleteSubLob,
    onDeleteBu = _a.onDeleteBu,
    refreshTrigger = _a.refreshTrigger,
    expandAllState = _a.expandAllState,
    onExpandedChange = _a.onExpandedChange;
  var t = useTranslation('orgConfig', {
    keyPrefix: 'clients'
  }).t;
  var _e = useState(true),
    isExpanded = _e[0],
    setIsExpanded = _e[1];
  var _f = useState(new Set()),
    expandedBuIds = _f[0],
    setExpandedBuIds = _f[1];
  var _g = useState(new Set()),
    expandedLobIds = _g[0],
    setExpandedLobIds = _g[1];
  var toggleClient = function () {
    return setIsExpanded(function (prev) {
      if (!prev) {
        setExpandedBuIds(new Set());
        setExpandedLobIds(new Set());
      }
      return !prev;
    });
  };
  var toggleLob = function (lobId) {
    return setExpandedLobIds(function (prev) {
      var next = new Set(prev);
      if (next.has(lobId)) next.delete(lobId);else next.add(lobId);
      return next;
    });
  };
  var _h = useClientLobsAPI(client.id),
    lobsData = _h[0],
    fetchLobs = _h[1];
  var _j = useClientBusinessUnitsAPI(client.id),
    busData = _j[0],
    fetchBus = _j[1];
  useEffect(function () {
    fetchLobs();
  }, [client.id, refreshTrigger, fetchLobs]);
  var hasIntegration = client.integrations.length > 0;
  useEffect(function () {
    if (hasIntegration) fetchBus();
  }, [client.id, hasIntegration, refreshTrigger, fetchBus]);
  var bus = useMemo(function () {
    var _a;
    return (_a = busData.data) !== null && _a !== void 0 ? _a : [];
  }, [busData.data]);
  var lobs = useMemo(function () {
    var _a;
    return (_a = lobsData.data) !== null && _a !== void 0 ? _a : [];
  }, [lobsData.data]);
  var lobsByBuId = useMemo(function () {
    var _a, _b, _c;
    var map = new Map();
    for (var _i = 0, _d = (_a = lobsData.data) !== null && _a !== void 0 ? _a : []; _i < _d.length; _i++) {
      var lob = _d[_i];
      var key = (_b = lob.buId) !== null && _b !== void 0 ? _b : null;
      map.set(key, __spreadArray(__spreadArray([], (_c = map.get(key)) !== null && _c !== void 0 ? _c : [], true), [lob], false));
    }
    return map;
  }, [lobsData.data]);
  var toggleBu = function (buId) {
    return setExpandedBuIds(function (prev) {
      var _a;
      var next = new Set(prev);
      if (next.has(buId)) {
        next.delete(buId);
      } else {
        next.add(buId);
        var childLobIds_1 = ((_a = lobsByBuId.get(buId)) !== null && _a !== void 0 ? _a : []).map(function (lob) {
          return lob.id;
        });
        setExpandedLobIds(function (prevLobs) {
          var nextLobs = new Set(prevLobs);
          for (var _i = 0, childLobIds_2 = childLobIds_1; _i < childLobIds_2.length; _i++) {
            var id = childLobIds_2[_i];
            nextLobs.delete(id);
          }
          return nextLobs;
        });
      }
      return next;
    });
  };
  var hasExpandableChildren = hasIntegration ? bus.length > 0 || ((_c = (_b = lobsByBuId.get(null)) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) > 0 : lobs.length > 0;
  var lastAppliedExpandAllVersion = useRef(0);
  useEffect(function () {
    if (expandAllState.version === 0) return;
    if (lastAppliedExpandAllVersion.current === expandAllState.version) return;
    if (lobsData.data === undefined || hasIntegration && busData.data === undefined) return;
    lastAppliedExpandAllVersion.current = expandAllState.version;
    setIsExpanded(expandAllState.expanded);
    if (!expandAllState.expanded) {
      setExpandedBuIds(new Set());
      setExpandedLobIds(new Set());
      return;
    }
    setExpandedBuIds(new Set(bus.map(function (bu) {
      return bu.id;
    })));
    setExpandedLobIds(new Set(lobs.filter(function (lob) {
      var _a;
      return (_a = lob.subLobs) === null || _a === void 0 ? void 0 : _a.length;
    }).map(function (lob) {
      return lob.id;
    })));
  }, [bus, busData.data, expandAllState.expanded, expandAllState.version, hasIntegration, lobs, lobsData.data]);
  useEffect(function () {
    onExpandedChange(client.id, isExpanded);
  }, [client.id, isExpanded, onExpandedChange]);
  // Connection label ("Aspect UK") keyed by connection id, so BU/LOB source hints show which named
  var connectionLabelById = useMemo(function () {
    var map = new Map();
    for (var _i = 0, _a = client.integrations; _i < _a.length; _i++) {
      var integration = _a[_i];
      map.set(integration.id, formatConnectionLabel(integration.integrationType, integration.name));
    }
    return map;
  }, [client.integrations]);
  // Source-connection hint shown in the Integration column for each child row. A BU carries its own
  // integrationId (labelled via the connection map, falling back to its type); a LOB inherits it from
  // its parent BU (manual, BU-less LOBs show "None"); a sub-LOB inherits from its parent LOB.
  var buSourceLabelByBuId = useMemo(function () {
    var _a, _b;
    var map = new Map();
    for (var _i = 0, _c = (_a = busData.data) !== null && _a !== void 0 ? _a : []; _i < _c.length; _i++) {
      var bu = _c[_i];
      map.set(bu.id, (_b = connectionLabelById.get(bu.integrationId)) !== null && _b !== void 0 ? _b : formatConnectionLabel(bu.integrationType));
    }
    return map;
  }, [busData.data, connectionLabelById]);
  var lobSourceLabel = function (lob) {
    var _a;
    return lob.buId == null ? t('integrationNone') : (_a = buSourceLabelByBuId.get(lob.buId)) !== null && _a !== void 0 ? _a : t('integrationNone');
  };
  var lobIconButtonSx = {
    color: 'text.primary',
    '&:hover': {
      color: 'text.primary',
      backgroundColor: 'action.hover'
    }
  };
  // A LOB renders as its own row (chevron to collapse its sub-LOBs + trash to delete the whole LOB).
  // Each sub-LOB renders as a separate indented child row with its own trash for individual deletion.
  var renderSubLobRow = function (lob, sub, nestLevel) {
    return _jsxs(StyledSubLobRow, {
      children: [_jsx(TableCell, __assign({
        sx: {
          pl: TABLE_HIERARCHY_INDENTS[nestLevel + 1]
        }
      }, {
        children: sub.subLobName
      })), _jsx(TableCell, {}), _jsx(SourceCell, {
        label: lobSourceLabel(lob)
      }), _jsx(TableCell, __assign({
        align: "center",
        sx: {
          p: 0
        }
      }, {
        children: _jsx(IconButton, __assign({
          size: "small",
          onClick: function () {
            return onDeleteSubLob(lob.id, sub.id);
          },
          sx: lobIconButtonSx
        }, {
          children: _jsx(FontAwesomeIcon, {
            icon: "trash-can"
          })
        }))
      }))]
    }, "sub-".concat(lob.id, "-").concat(sub.id));
  };
  var renderLobRows = function (lobList, nestLevel) {
    return lobList.flatMap(function (lob) {
      var _a;
      var hasSubLobs = !!((_a = lob.subLobs) === null || _a === void 0 ? void 0 : _a.length);
      var lobExpanded = expandedLobIds.has(lob.id);
      var lobRow = _jsxs(StyledLobRow, {
        children: [_jsx(TableCell, __assign({
          sx: {
            pl: TABLE_HIERARCHY_INDENTS[nestLevel]
          }
        }, {
          children: _jsx(ExpandableRowLabel, __assign({
            expanded: lobExpanded,
            onToggle: hasSubLobs ? function () {
              return toggleLob(lob.id);
            } : undefined
          }, {
            children: lob.lobName
          }))
        })), _jsx(TableCell, {}), _jsx(SourceCell, {
          label: lobSourceLabel(lob)
        }), _jsx(TableCell, __assign({
          align: "center",
          sx: {
            p: 0
          }
        }, {
          children: _jsx(IconButton, __assign({
            size: "small",
            onClick: function () {
              return onDeleteLob(lob.id);
            },
            sx: lobIconButtonSx
          }, {
            children: _jsx(FontAwesomeIcon, {
              icon: "trash-can"
            })
          }))
        }))]
      }, "lob-".concat(lob.id));
      if (!hasSubLobs || !lobExpanded) return [lobRow];
      return __spreadArray([lobRow], lob.subLobs.map(function (sub) {
        return renderSubLobRow(lob, sub, nestLevel);
      }), true);
    });
  };
  return _jsxs(_Fragment, {
    children: [_jsxs(StyledClientRow, {
      children: [_jsx(TableCell, __assign({
        sx: {
          pl: TABLE_HIERARCHY_INDENTS[1]
        }
      }, {
        children: _jsx(ExpandableRowLabel, __assign({
          expanded: isExpanded,
          onToggle: hasExpandableChildren ? toggleClient : undefined
        }, {
          children: _jsx(MuiLink, __assign({
            component: "button",
            onClick: function (e) {
              e.stopPropagation();
              onEdit();
            },
            sx: {
              fontStyle: client.isHqClient ? 'italic' : 'normal'
            }
          }, {
            children: client.clientName
          }))
        }))
      })), _jsx(TableCell, {
        children: client.industryName
      }), _jsx(TableCell, {
        children: _jsx(Stack, __assign({
          spacing: 0.5,
          sx: {
            width: '100%'
          }
        }, {
          children: client.integrations.length === 0 ? _jsxs(Stack, __assign({
            direction: "row",
            alignItems: "center",
            justifyContent: "space-between"
          }, {
            children: [_jsx(Box, __assign({
              component: "span",
              sx: {
                color: 'text.disabled'
              }
            }, {
              children: t('integrationNone')
            })), _jsx(MuiLink, __assign({
              component: "button",
              type: "button",
              onClick: onAddIntegration
            }, {
              children: t('addIntegration')
            }))]
          })) : client.integrations.map(function (integration, idx) {
            return _jsxs(Stack, __assign({
              direction: "row",
              alignItems: "center",
              justifyContent: "space-between"
            }, {
              children: [_jsxs(Stack, __assign({
                direction: "row",
                alignItems: "center",
                spacing: 1
              }, {
                children: [_jsx(MuiLink, __assign({
                  component: "button",
                  type: "button",
                  onClick: function () {
                    return onEditIntegration(integration.id);
                  },
                  sx: {
                    textAlign: 'left'
                  }
                }, {
                  children: formatConnectionLabel(integration.integrationType, integration.name)
                })), _jsx(IntegrationStatusIcon, {
                  status: integration.integrationStatus,
                  lastConnectionTest: integration.lastConnectionTest
                })]
              })), idx === client.integrations.length - 1 && _jsx(MuiLink, __assign({
                component: "button",
                type: "button",
                onClick: onAddIntegration
              }, {
                children: t('addIntegration')
              }))]
            }), integration.id);
          })
        }))
      }), _jsx(TableCell, __assign({
        align: "center",
        sx: {
          p: 0
        }
      }, {
        children: _jsx(Tooltip, __assign({
          title: client.isHqClient ? t('hqCannotDelete') : t('deleteClientTooltip')
        }, {
          children: _jsx("span", {
            children: _jsx(IconButton, __assign({
              size: "small",
              onClick: onDelete,
              disabled: client.isHqClient,
              sx: {
                color: function (theme) {
                  return theme.palette.text.primary;
                },
                '&:hover': {
                  color: function (theme) {
                    return theme.palette.text.primary;
                  },
                  backgroundColor: function (theme) {
                    return theme.palette.action.hover;
                  }
                }
              }
            }, {
              children: _jsx(FontAwesomeIcon, {
                icon: "trash-can"
              })
            }))
          })
        }))
      }))]
    }), isExpanded && (hasIntegration ? _jsxs(_Fragment, {
      children: [bus.map(function (bu) {
        var _a, _b;
        var buLobs = (_a = lobsByBuId.get(bu.id)) !== null && _a !== void 0 ? _a : [];
        var buExpanded = expandedBuIds.has(bu.id);
        return _jsxs(Fragment, {
          children: [_jsxs(StyledBuRow, {
            children: [_jsx(TableCell, __assign({
              sx: {
                pl: TABLE_HIERARCHY_INDENTS[2]
              }
            }, {
              children: _jsx(ExpandableRowLabel, __assign({
                expanded: buExpanded,
                onToggle: buLobs.length > 0 ? function () {
                  return toggleBu(bu.id);
                } : undefined
              }, {
                children: bu.buName
              }))
            })), _jsx(TableCell, {}), _jsx(SourceCell, {
              label: (_b = buSourceLabelByBuId.get(bu.id)) !== null && _b !== void 0 ? _b : t('integrationNone')
            }), _jsx(TableCell, __assign({
              align: "center",
              sx: {
                p: 0
              }
            }, {
              children: _jsx(IconButton, __assign({
                size: "small",
                onClick: function () {
                  return onDeleteBu(bu.id);
                },
                sx: lobIconButtonSx
              }, {
                children: _jsx(FontAwesomeIcon, {
                  icon: "trash-can"
                })
              }))
            }))]
          }), buExpanded && renderLobRows(buLobs, 3)]
        }, "bu-".concat(bu.id));
      }), renderLobRows((_d = lobsByBuId.get(null)) !== null && _d !== void 0 ? _d : [], 2)]
    }) : renderLobRows(lobs, 2))]
  });
}
export var ClientsLobsStep = function () {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j;
  var _k = useContext(OrgConfigWizardContext),
    configVersionId = _k.configVersionId,
    isBpo = _k.isBpo,
    versionLoading = _k.versionLoading,
    versionError = _k.versionError;
  var t = useTranslation('orgConfig', {
    keyPrefix: 'clients'
  }).t;
  var tForm = useTranslation('translation', {
    keyPrefix: 'form'
  }).t;
  var _l = useState(null),
    headerRightEl = _l[0],
    setHeaderRightEl = _l[1];
  useEffect(function () {
    setHeaderRightEl(document.getElementById('org-config-clients-lobs-header-top-right'));
  }, []);
  var _m = useClientsAPI(configVersionId),
    clientsData = _m[0],
    fetchClients = _m[1];
  var _o = useState(0),
    lobRefreshTrigger = _o[0],
    setLobRefreshTrigger = _o[1];
  var _p = useState({
      expanded: true,
      version: 0
    }),
    expandAllState = _p[0],
    setExpandAllState = _p[1];
  var _q = useState({}),
    expandedClientStates = _q[0],
    setExpandedClientStates = _q[1];
  var _r = useDialog(),
    dialogMode = _r.dialogMode,
    dialogData = _r.data,
    handleOpenDialog = _r.handleOpenDialog,
    handleCloseDialog = _r.handleCloseDialog;
  var initialClientsRef = useRef(null);
  var hasMadeChangesRef = useRef(false);
  useEffect(function () {
    if (clientsData.data && !initialClientsRef.current) {
      initialClientsRef.current = clientsData.data;
    }
  }, [clientsData.data]);
  var checkIsDirty = useCallback(function () {
    if (!initialClientsRef.current) return false;
    var isDirty = JSON.stringify(clientsData.data) !== JSON.stringify(initialClientsRef.current);
    return hasMadeChangesRef.current || isDirty;
  }, [clientsData.data]);
  useOrgConfigDirtyRegister(checkIsDirty);
  var _s = getDeleteDialogIds(dialogMode, dialogData),
    deleteClientId = _s.deleteClientId,
    deleteLobClientId = _s.deleteLobClientId,
    deleteLobId = _s.deleteLobId,
    deleteBuClientId = _s.deleteBuClientId,
    deleteBuId = _s.deleteBuId,
    deleteSubLobClientId = _s.deleteSubLobClientId,
    deleteSubLobLobId = _s.deleteSubLobLobId,
    deleteSubLobId = _s.deleteSubLobId;
  var _t = useDeleteClientAPI(deleteClientId),
    deleteClientData = _t[0],
    deleteClient = _t[1],
    clearDeleteClient = _t[2];
  var _u = useDeleteLobAPI(deleteLobClientId, deleteLobId),
    deleteLobData = _u[0],
    deleteLob = _u[1],
    clearDeleteLob = _u[2];
  var _v = useDeleteBusinessUnitAPI(deleteBuClientId, deleteBuId),
    deleteBuData = _v[0],
    deleteBu = _v[1],
    clearDeleteBu = _v[2];
  var _w = useDeleteSubLobAPI(deleteSubLobClientId, deleteSubLobLobId, deleteSubLobId),
    deleteSubLobData = _w[0],
    deleteSubLob = _w[1],
    clearDeleteSubLob = _w[2];
  var lobSchema = yup.object({
    clientId: yup.number().required().min(1).label(t('lobPanel.client')),
    buId: yup.number().nullable().notRequired().transform(function (v) {
      return v === '' || Number.isNaN(v) ? null : v;
    }),
    lobSelect: yup.string().notRequired(),
    lobName: yup.string().trim().required().label(t('lobPanel.lobName')),
    subLobSelect: yup.string().notRequired(),
    subLobName: yup.string().notRequired()
  });
  var _x = useForm({
      defaultValues: BLANK_LOB_FORM,
      resolver: yupResolver(lobSchema)
    }),
    lobControl = _x.control,
    lobHandleSubmit = _x.handleSubmit,
    lobReset = _x.reset,
    lobWatch = _x.watch,
    lobSetError = _x.setError,
    lobClearErrors = _x.clearErrors,
    lobSetValue = _x.setValue,
    lobErrors = _x.formState.errors;
  var watchedClientId = lobWatch('clientId');
  var selectedClientId = Number(watchedClientId) || 0;
  var _y = useCreateLobAPI(selectedClientId),
    createLobData = _y[0],
    createLob = _y[1],
    clearCreateLob = _y[2];
  var _z = useUpdateLobAPI(selectedClientId),
    updateLobData = _z[0],
    updateLob = _z[1],
    clearUpdateLob = _z[2];
  var _0 = useClientLobsAPI(selectedClientId, 'orgConfigAddPanelLobs'),
    selectedLobsData = _0[0],
    fetchSelectedLobs = _0[1];
  var _1 = useClientBusinessUnitsAPI(selectedClientId, 'orgConfigAddPanelBus'),
    busData = _1[0],
    fetchBus = _1[1];
  var clients = useMemo(function () {
    var _a;
    var data = (_a = clientsData.data) !== null && _a !== void 0 ? _a : [];
    return __spreadArray([], data, true).sort(function (a, b) {
      return (b.isHqClient ? 1 : 0) - (a.isHqClient ? 1 : 0);
    });
  }, [clientsData.data]);
  var hasAnyExpandedItems = clients.some(function (client) {
    var _a;
    return (_a = expandedClientStates[client.id]) !== null && _a !== void 0 ? _a : true;
  });
  var selectedClient = useMemo(function () {
    var _a;
    return (_a = clients.find(function (c) {
      return c.id === selectedClientId;
    })) !== null && _a !== void 0 ? _a : null;
  }, [clients, selectedClientId]);
  var isIntegratedClient = ((_b = (_a = selectedClient === null || selectedClient === void 0 ? void 0 : selectedClient.integrations) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0;
  var availableBus = (_c = busData.data) !== null && _c !== void 0 ? _c : [];
  var connectionLabelById = useMemo(function () {
    var _a;
    var map = new Map();
    for (var _i = 0, _b = (_a = selectedClient === null || selectedClient === void 0 ? void 0 : selectedClient.integrations) !== null && _a !== void 0 ? _a : []; _i < _b.length; _i++) {
      var integration = _b[_i];
      map.set(integration.id, formatConnectionLabel(integration.integrationType, integration.name));
    }
    return map;
  }, [selectedClient]);
  var showGroupSource = ((_e = (_d = selectedClient === null || selectedClient === void 0 ? void 0 : selectedClient.integrations) === null || _d === void 0 ? void 0 : _d.length) !== null && _e !== void 0 ? _e : 0) > 0;
  var groupSourceLabel = function (bu) {
    var _a;
    return (_a = connectionLabelById.get(bu.integrationId)) !== null && _a !== void 0 ? _a : formatConnectionLabel(bu.integrationType);
  };
  var lobNameOptions = useMemo(function () {
    var _a;
    return __spreadArray(__spreadArray([], ((_a = selectedLobsData.data) !== null && _a !== void 0 ? _a : []).map(function (lob) {
      return {
        id: lob.lobName,
        name: lob.lobName
      };
    }), true), [{
      id: NEW_LOB_OPTION,
      name: t('lobPanel.newLob')
    }], false);
  }, [selectedLobsData.data, t]);
  var watchedLobSelect = lobWatch('lobSelect');
  var isNewLob = watchedLobSelect === NEW_LOB_OPTION;
  var selectedExistingLob = useMemo(function () {
    var _a, _b;
    return isNewLob || !watchedLobSelect ? null : (_b = ((_a = selectedLobsData.data) !== null && _a !== void 0 ? _a : []).find(function (lob) {
      return lob.lobName === watchedLobSelect;
    })) !== null && _b !== void 0 ? _b : null;
  }, [isNewLob, watchedLobSelect, selectedLobsData.data]);
  var subLobNameOptions = useMemo(function () {
    var _a;
    return __spreadArray(__spreadArray([], ((_a = selectedExistingLob === null || selectedExistingLob === void 0 ? void 0 : selectedExistingLob.subLobs) !== null && _a !== void 0 ? _a : []).map(function (sub) {
      return {
        id: sub.subLobName,
        name: sub.subLobName
      };
    }), true), [{
      id: NEW_SUBLOB_OPTION,
      name: t('lobPanel.newSubLob')
    }], false);
  }, [selectedExistingLob, t]);
  var isNewSubLob = lobWatch('subLobSelect') === NEW_SUBLOB_OPTION;
  var refresh = useCallback(function () {
    if (configVersionId) fetchClients();
    setLobRefreshTrigger(function (n) {
      return n + 1;
    });
  }, [configVersionId, fetchClients]);
  useEffect(function () {
    if (configVersionId) fetchClients();
  }, [configVersionId, fetchClients]);
  useEffect(function () {
    if (!isBpo && clients.length > 0) {
      var hqClient = clients.find(function (c) {
        return c.isHqClient;
      });
      if (hqClient) lobReset(__assign(__assign({}, BLANK_LOB_FORM), {
        clientId: hqClient.id
      }));
    }
  }, [isBpo, clients, lobReset]);
  useEffect(function () {
    lobSetValue('lobSelect', '');
    lobSetValue('lobName', '');
    lobSetValue('buId', '');
  }, [selectedClientId, lobSetValue]);
  useEffect(function () {
    lobSetValue('subLobSelect', '');
    lobSetValue('subLobName', '');
  }, [watchedLobSelect, lobSetValue]);
  useEffect(function () {
    if (selectedClientId) fetchSelectedLobs();
  }, [selectedClientId, lobRefreshTrigger, fetchSelectedLobs]);
  useEffect(function () {
    if (selectedClientId && isIntegratedClient) fetchBus();
  }, [selectedClientId, isIntegratedClient, lobRefreshTrigger, fetchBus]);
  useEffect(function () {
    if (createLobData.data) {
      refresh();
      lobReset(__assign(__assign({}, BLANK_LOB_FORM), {
        clientId: watchedClientId
      }));
    }
    return clearCreateLob;
  }, [createLobData.data, refresh, clearCreateLob, lobReset, watchedClientId]);
  useEffect(function () {
    if (updateLobData.data) {
      refresh();
      lobReset(__assign(__assign({}, BLANK_LOB_FORM), {
        clientId: watchedClientId
      }));
    }
    return clearUpdateLob;
  }, [updateLobData.data, refresh, clearUpdateLob, lobReset, watchedClientId]);
  useEffect(function () {
    if (!is2xxSuccess(deleteClientData.statusCode)) return;
    refresh();
    handleCloseDialog();
    clearDeleteClient();
  }, [deleteClientData.statusCode, refresh, handleCloseDialog, clearDeleteClient]);
  useEffect(function () {
    if (!is2xxSuccess(deleteLobData.statusCode)) return;
    refresh();
    handleCloseDialog();
    clearDeleteLob();
  }, [deleteLobData.statusCode, refresh, handleCloseDialog, clearDeleteLob]);
  useEffect(function () {
    if (!is2xxSuccess(deleteBuData.statusCode)) return;
    refresh();
    handleCloseDialog();
    clearDeleteBu();
  }, [deleteBuData.statusCode, refresh, handleCloseDialog, clearDeleteBu]);
  useEffect(function () {
    if (!is2xxSuccess(deleteSubLobData.statusCode)) return;
    refresh();
    handleCloseDialog();
    clearDeleteSubLob();
  }, [deleteSubLobData.statusCode, refresh, handleCloseDialog, clearDeleteSubLob]);
  var handleAddLob = function (_a) {
    var _b, _c, _d;
    var lobName = _a.lobName,
      subLobName = _a.subLobName,
      buId = _a.buId;
    lobClearErrors(['lobName', 'subLobName']);
    var cleanedLob = cleanName(lobName);
    var cleanedSub = subLobName ? cleanName(subLobName) : '';
    var resolvedBuId = Number(buId) || null;
    // Match an existing LOB ignoring case and internal/edge whitespace so spacing/case variations
    // can never create a near-duplicate record.
    var existingLob = ((_b = selectedLobsData.data) !== null && _b !== void 0 ? _b : []).find(function (l) {
      return normalizeName(l.lobName) === normalizeName(cleanedLob) && (resolvedBuId === null || l.buId === resolvedBuId);
    });
    // Brand-new LOB: create it (with an optional first sub line of business).
    if (!existingLob) {
      createLob({
        lobName: cleanedLob,
        buId: resolvedBuId,
        subLobs: cleanedSub ? [{
          subLobName: cleanedSub
        }] : []
      });
      return;
    }
    // The LOB already exists. Re-adding it with no sub line of business is a duplicate.
    if (!cleanedSub) {
      lobSetError('lobName', {
        message: t('lobPanel.lobExists')
      });
      return;
    }
    // The sub line of business already exists under this LOB → duplicate.
    var subAlreadyExists = ((_c = existingLob.subLobs) !== null && _c !== void 0 ? _c : []).some(function (s) {
      return normalizeName(s.subLobName) === normalizeName(cleanedSub);
    });
    if (subAlreadyExists) {
      lobSetError('subLobName', {
        message: t('lobPanel.subLobExists')
      });
      return;
    }
    // Existing LOB + a genuinely new sub line of business → add it to the existing LOB.
    var mergedSubLobs = __spreadArray(__spreadArray([], ((_d = existingLob.subLobs) !== null && _d !== void 0 ? _d : []).map(function (s) {
      return {
        subLobId: s.id,
        subLobName: s.subLobName
      };
    }), true), [{
      subLobName: cleanedSub
    }], false);
    hasMadeChangesRef.current = true;
    updateLob({
      subLobs: mergedSubLobs
    }, {
      lobId: existingLob.id
    });
  };
  var handleClearLobForm = function () {
    lobClearErrors(['lobName', 'subLobName']);
    lobReset(BLANK_LOB_FORM);
  };
  var handleDeleteClient = function () {
    if (dialogMode === DialogMode.DeleteClient) deleteClient();
  };
  var handleDeleteLob = function () {
    if (dialogMode === DialogMode.DeleteLob) deleteLob();
  };
  var handleDeleteBu = function () {
    if (dialogMode === DialogMode.DeleteBu) deleteBu();
  };
  var handleDeleteSubLob = function () {
    if (dialogMode === DialogMode.DeleteSubLob) deleteSubLob();
  };
  var handleClientExpandedChange = useCallback(function (clientId, expanded) {
    setExpandedClientStates(function (prev) {
      var _a;
      return __assign(__assign({}, prev), (_a = {}, _a[clientId] = expanded, _a));
    });
  }, []);
  var handleExpandAllToggle = function () {
    var expanded = !hasAnyExpandedItems;
    setExpandAllState(function (prev) {
      return {
        expanded: expanded,
        version: prev.version + 1
      };
    });
    setExpandedClientStates(Object.fromEntries(clients.map(function (client) {
      return [client.id, expanded];
    })));
  };
  var deleteClientName = (_g = (_f = clients.find(function (c) {
    return c.id === (dialogData === null || dialogData === void 0 ? void 0 : dialogData.clientId);
  })) === null || _f === void 0 ? void 0 : _f.clientName) !== null && _g !== void 0 ? _g : '';
  var clientModalMode;
  if (dialogMode === DialogMode.EditClient) {
    clientModalMode = 'edit-details';
  } else if (dialogMode === DialogMode.AddIntegration) {
    clientModalMode = 'add-integration';
  } else if (dialogMode === DialogMode.EditIntegration) {
    clientModalMode = 'edit-integration';
  } else {
    clientModalMode = 'add-client';
  }
  if (versionLoading) return _jsx(FallbackComponent, {
    requesting: true,
    overlay: true
  });
  if (versionError) {
    return _jsx(Typography, __assign({
      color: "error"
    }, {
      children: (_h = versionError.message) !== null && _h !== void 0 ? _h : 'Failed to load configuration'
    }));
  }
  if (!configVersionId) return _jsx(FallbackComponent, {
    requesting: true,
    overlay: true
  });
  return _jsxs(_Fragment, {
    children: [headerRightEl && createPortal(_jsx(Tooltip, __assign({
      title: isBpo ? '' : t('bpoOnlyTooltip')
    }, {
      children: _jsx("span", {
        children: _jsx(AddClientButton, __assign({
          variant: "outlined",
          onClick: function () {
            return handleOpenDialog(DialogMode.AddClient);
          },
          disabled: !isBpo
        }, {
          children: t('addClientButton')
        }))
      })
    })), headerRightEl), _jsxs(Stack, __assign({
      spacing: 3
    }, {
      children: [_jsx(ErrorMessageBox, {
        errors: [createLobData.error, updateLobData.error]
      }), _jsx(AddLobPanel, {
        children: _jsxs(AddLobFormLayout, {
          children: [_jsx(FormField, __assign({
            name: "clientId",
            label: t('lobPanel.client'),
            required: true,
            control: lobControl,
            sx: ADD_LOB_FIELD_SX
          }, {
            children: _jsx(Autocomplete, {
              options: clients.map(function (c) {
                return {
                  id: c.id,
                  name: c.clientName
                };
              }),
              placeholder: t('lobPanel.pleaseSelect'),
              disableClearable: true
            })
          })), _jsx(FormField, __assign({
            name: "buId",
            label: t('lobPanel.groups'),
            control: lobControl,
            sx: ADD_LOB_FIELD_SX
          }, {
            children: _jsx(Autocomplete, {
              options: availableBus.map(function (bu) {
                return {
                  id: bu.id,
                  name: bu.buName
                };
              }),
              placeholder: t('lobPanel.noGroup'),
              disableClearable: false,
              disabled: !isIntegratedClient,
              renderOption: function (props, option) {
                var bu = availableBus.find(function (b) {
                  return b.id === option.id;
                });
                return _createElement("li", __assign({}, props, {
                  key: option.id,
                  style: {
                    display: 'flex',
                    width: '100%'
                  }
                }), _jsx("span", {
                  children: option.name
                }), showGroupSource && bu && _jsx(Box, __assign({
                  component: "span",
                  sx: {
                    color: 'text.disabled',
                    ml: 'auto',
                    pl: 1
                  }
                }, {
                  children: groupSourceLabel(bu)
                })));
              }
            })
          })), _jsxs(Box, __assign({
            sx: ADD_LOB_FIELD_SX
          }, {
            children: [_jsx(InputLabel, __assign({
              required: true
            }, {
              children: t('lobPanel.lobName')
            })), _jsx(SelectOrCustomField, {
              required: true,
              isCustom: isNewLob,
              control: lobControl,
              name: "lobSelect",
              customName: "lobName",
              options: lobNameOptions,
              defaultValue: "",
              placeholder: t('lobPanel.selectLob'),
              customPlaceholder: t('lobPanel.lobNamePlaceholder'),
              customValue: lobWatch('lobName'),
              onCustomChange: function (value) {
                return lobSetValue('lobName', value);
              },
              onSelectChange: function () {
                return lobSetValue('lobSelect', '');
              },
              onChange: function (value) {
                lobClearErrors(['lobName']);
                lobSetValue('lobName', value === NEW_LOB_OPTION ? '' : value !== null && value !== void 0 ? value : '');
              },
              maxLength: MAX_TEXT_LENGTH
            }), lobErrors.lobName && !isNewLob && _jsx(FormHelperText, __assign({
              error: true
            }, {
              children: tForm(lobErrors.lobName.message, {
                defaultValue: lobErrors.lobName.message
              })
            }))]
          })), _jsxs(Box, __assign({
            sx: ADD_LOB_FIELD_SX
          }, {
            children: [_jsx(InputLabel, {
              children: t('lobPanel.subLobName')
            }), _jsx(SelectOrCustomField, {
              isCustom: isNewSubLob,
              control: lobControl,
              name: "subLobSelect",
              customName: "subLobName",
              options: subLobNameOptions,
              defaultValue: "",
              placeholder: t('lobPanel.selectSubLob'),
              customPlaceholder: t('lobPanel.subLobNamePlaceholder'),
              customValue: lobWatch('subLobName'),
              onCustomChange: function (value) {
                return lobSetValue('subLobName', value);
              },
              onSelectChange: function () {
                return lobSetValue('subLobSelect', '');
              },
              onChange: function (value) {
                lobClearErrors(['subLobName']);
                lobSetValue('subLobName', value === NEW_SUBLOB_OPTION ? '' : value !== null && value !== void 0 ? value : '');
              },
              disableClearable: false,
              maxLength: MAX_TEXT_LENGTH
            }), lobErrors.subLobName && !isNewSubLob && _jsx(FormHelperText, __assign({
              error: true
            }, {
              children: tForm(lobErrors.subLobName.message, {
                defaultValue: lobErrors.subLobName.message
              })
            }))]
          })), _jsxs(Box, __assign({
            sx: {
              alignSelf: 'flex-start',
              flexShrink: 0,
              width: {
                xs: '100%',
                sm: 'auto'
              }
            }
          }, {
            children: [_jsx(InputLabel, __assign({
              hidden: true
            }, {
              children: t('lobPanel.actionsLabel')
            })), _jsxs(Stack, __assign({
              direction: "row",
              spacing: 2,
              alignItems: "center",
              flexWrap: "wrap"
            }, {
              children: [_jsx(LoadingButton, __assign({
                variant: "outlined",
                onClick: lobHandleSubmit(handleAddLob),
                disabled: !!createLobData.requesting || !!updateLobData.requesting,
                loading: !!createLobData.requesting || !!updateLobData.requesting
              }, {
                children: t('lobPanel.add')
              })), _jsx(MuiLink, __assign({
                component: "button",
                type: "button",
                onClick: handleClearLobForm
              }, {
                children: t('lobPanel.clearFields')
              }))]
            }))]
          }))]
        })
      }), _jsx(FallbackComponent, __assign({
        requesting: !!clientsData.requesting,
        overlay: true
      }, {
        children: _jsx(WizardTable, __assign({
          headers: _jsxs(_Fragment, {
            children: [_jsx(TableCell, {
              children: _jsxs(Stack, __assign({
                direction: "row",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%"
              }, {
                children: [_jsx("span", {
                  children: t('table.client')
                }), clients.length > 0 && _jsx(Tooltip, __assign({
                  title: hasAnyExpandedItems ? t('collapse', {
                    defaultValue: 'Collapse'
                  }) : t('expand', {
                    defaultValue: 'Expand'
                  }),
                  placement: "top"
                }, {
                  children: _jsx(MuiLink, __assign({
                    component: "button",
                    onClick: function (e) {
                      e.stopPropagation();
                      handleExpandAllToggle();
                    },
                    sx: {
                      fontWeight: 'normal',
                      color: 'white',
                      ':hover': {
                        color: 'white'
                      }
                    }
                  }, {
                    children: _jsx(FontAwesomeIcon, {
                      icon: hasAnyExpandedItems ? faMinimize : faMaximize
                    })
                  }))
                }))]
              }))
            }), _jsx(TableCell, __assign({
              sx: {
                width: '20%'
              }
            }, {
              children: t('table.industry')
            })), _jsx(TableCell, __assign({
              sx: {
                width: '20%'
              }
            }, {
              children: t('table.integration')
            })), _jsx(TableCell, {
              sx: {
                width: 48
              },
              align: "center"
            })]
          })
        }, {
          children: _jsxs(TableBody, {
            children: [clients.length === 0 && _jsx(TableRow, {
              children: _jsx(TableCell, __assign({
                colSpan: 4,
                align: "center",
                sx: {
                  py: 6,
                  color: 'text.secondary'
                }
              }, {
                children: t('table.noData')
              }))
            }), clients.map(function (client) {
              return _jsx(ClientRows, {
                client: client,
                onEdit: function () {
                  return handleOpenDialog(DialogMode.EditClient, {
                    client: client
                  });
                },
                onAddIntegration: function () {
                  return handleOpenDialog(DialogMode.AddIntegration, {
                    client: client
                  });
                },
                onEditIntegration: function (integrationId) {
                  return handleOpenDialog(DialogMode.EditIntegration, {
                    client: client,
                    integrationId: integrationId
                  });
                },
                onDelete: function () {
                  return handleOpenDialog(DialogMode.DeleteClient, {
                    clientId: client.id
                  });
                },
                onDeleteLob: function (lobId) {
                  return handleOpenDialog(DialogMode.DeleteLob, {
                    clientId: client.id,
                    lobId: lobId
                  });
                },
                onDeleteSubLob: function (lobId, subLobId) {
                  return handleOpenDialog(DialogMode.DeleteSubLob, {
                    clientId: client.id,
                    lobId: lobId,
                    subLobId: subLobId
                  });
                },
                onDeleteBu: function (buId) {
                  return handleOpenDialog(DialogMode.DeleteBu, {
                    clientId: client.id,
                    buId: buId
                  });
                },
                refreshTrigger: lobRefreshTrigger,
                expandAllState: expandAllState,
                onExpandedChange: handleClientExpandedChange
              }, client.id);
            })]
          })
        }))
      }))]
    })), _jsx(AddClientModal, {
      open: dialogMode === DialogMode.AddClient || dialogMode === DialogMode.EditClient || dialogMode === DialogMode.AddIntegration || dialogMode === DialogMode.EditIntegration,
      onClose: handleCloseDialog,
      onSaved: refresh,
      configVersionId: configVersionId,
      mode: clientModalMode,
      editClient: dialogMode === DialogMode.AddClient ? null : (_j = dialogData === null || dialogData === void 0 ? void 0 : dialogData.client) !== null && _j !== void 0 ? _j : null,
      targetIntegrationId: dialogMode === DialogMode.EditIntegration ? dialogData === null || dialogData === void 0 ? void 0 : dialogData.integrationId : undefined
    }), _jsx(ConfirmDialog, {
      open: dialogMode === DialogMode.DeleteClient,
      onClose: handleCloseDialog,
      title: t('deleteClient.title'),
      message: dialogMode === DialogMode.DeleteClient ? t('deleteClient.message', {
        clientName: deleteClientName
      }) : '',
      onConfirm: handleDeleteClient,
      errors: [deleteClientData.error]
    }), _jsx(ConfirmDialog, {
      open: dialogMode === DialogMode.DeleteLob,
      onClose: handleCloseDialog,
      title: t('deleteLob.title'),
      message: t('deleteLob.message'),
      onConfirm: handleDeleteLob,
      errors: [deleteLobData.error]
    }), _jsx(ConfirmDialog, {
      open: dialogMode === DialogMode.DeleteSubLob,
      onClose: handleCloseDialog,
      title: t('deleteSubLob.title'),
      message: t('deleteSubLob.message'),
      onConfirm: handleDeleteSubLob,
      errors: [deleteSubLobData.error]
    }), _jsx(ConfirmDialog, {
      open: dialogMode === DialogMode.DeleteBu,
      onClose: handleCloseDialog,
      title: t('deleteBu.title'),
      message: t('deleteBu.message'),
      onConfirm: handleDeleteBu,
      errors: [deleteBuData.error]
    })]
  });
};